import {
  ARTIST_PORTAL_CAPTURE_VERSION_PENDING_SIGNIN,
  ArtistPortalSyncStatus,
} from 'fym-shared/src/smartlinks/artistPortals';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';

/**
 * The states of a card whose login happens on the page of the service inside the Music24 app
 * (Ditto Music, `sessionCaptureAvailable`). design: your-intelligence k1.
 */
export enum SessionCaptureCardState {
  notConnected = 'notConnected',
  /** Signed in, the first read runs: no number yet. */
  firstRead = 'firstRead',
  connected = 'connected',
  /** The service signed Music24 out. */
  reconnect = 'reconnect',
  /** Signed in, no artist chosen. */
  needsArtist = 'needsArtist',
}

/**
 * True when the web shows the phone handoff for this card instead of the waiting list: the
 * sign-in of the service runs on its own page in the Music24 app. Ditto Music says so with the
 * legacy `sessionCaptureAvailable`; every newer service (DistroKid, TuneCore, CD Baby, the
 * sign-in-only portals) with `sessionCapture.available`.
 */
export function isSessionCaptureCard(connection: ServiceConnection): boolean {
  return connection.sessionCaptureAvailable === true || connection.sessionCapture?.available === true;
}

/** The Ditto Music card: it keeps its own copy (the Google hint, "stores and earnings"). */
export function isDittoCaptureCard(connection: ServiceConnection): boolean {
  return connection.sessionCaptureAvailable === true;
}

/**
 * A sign-in-only service (capture version 4 and up): the sign-in is stored and Music24 reads no
 * number yet. Its card has no artist picker and no Streaming link. A service whose reader is live
 * (`readsNumbers`, Apple Music for Artists since 2026-10-21) keeps the sign-in flow and shows its
 * artist and numbers like any reader.
 */
export function isSignInOnlyCaptureCard(connection: ServiceConnection): boolean {
  return (
    (connection.sessionCapture?.version ?? 0) >= ARTIST_PORTAL_CAPTURE_VERSION_PENDING_SIGNIN &&
    connection.readsNumbers !== true
  );
}

export function sessionCaptureCardState(connection: ServiceConnection): SessionCaptureCardState {
  if (connection.status === ServiceConnectionStatus.expired) {
    return SessionCaptureCardState.reconnect;
  }
  if (connection.status !== ServiceConnectionStatus.connected) {
    return SessionCaptureCardState.notConnected;
  }
  if (connection.artist === null) {
    return SessionCaptureCardState.needsArtist;
  }
  const isReading =
    connection.syncStatus === ArtistPortalSyncStatus.queued ||
    connection.syncStatus === ArtistPortalSyncStatus.running ||
    connection.syncStatus === ArtistPortalSyncStatus.idle;
  if (connection.lastSyncAt === null && isReading) {
    return SessionCaptureCardState.firstRead;
  }
  return SessionCaptureCardState.connected;
}

/**
 * The day the numbers stop: the newest day with numbers, else the day of the last read. Null when
 * neither is known.
 */
export function numbersStopDay(connection: ServiceConnection): string | null {
  return connection.syncedThrough ?? connection.lastSyncAt;
}
